import { useEffect, useMemo, useState, useCallback } from "react";
import { reverseGeocode, labelKey } from "../utils/reverseGeocode";

/**
 * Resolves human-readable place labels for a list of coordinates.
 *
 * Input: array of { lat, lng } (nulls allowed and skipped).
 * Output:
 *   labels : { [key]: string | null }
 *            key is labelKey(lat, lng). `null` means lookup finished with
 *            no usable result. Missing key means "still pending".
 *   getLabel(lat, lng) : convenience reader returning the label, `null`,
 *                        or `undefined` while still loading.
 *
 * Lookups are serialized at the module level inside reverseGeocode() so
 * multiple hook instances on the same page share cache + rate limiter.
 */
export const useReverseGeocoded = (coords) => {
  const [labels, setLabels] = useState({});

  // Stable signature so the effect only runs when the coordinate set
  // actually changes, not on every parent render.
  const signature = useMemo(
    () =>
      (coords || [])
        .map((c) =>
          c && typeof c.lat === "number" && typeof c.lng === "number"
            ? labelKey(c.lat, c.lng)
            : ""
        )
        .join("|"),
    [coords]
  );

  useEffect(() => {
    if (!coords || coords.length === 0) return undefined;
    let cancelled = false;

    (async () => {
      for (const c of coords) {
        if (cancelled) return;
        if (!c || typeof c.lat !== "number" || typeof c.lng !== "number") {
          continue;
        }
        const key = labelKey(c.lat, c.lng);
        // Skip if we've already resolved (or started resolving) this key
        // in a previous pass within this hook instance.
        if (Object.prototype.hasOwnProperty.call(labels, key)) continue;

        const label = await reverseGeocode(c.lat, c.lng);
        if (cancelled) return;
        setLabels((prev) => {
          if (prev[key] === label) return prev;
          return { ...prev, [key]: label };
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const getLabel = useCallback(
    (lat, lng) => {
      if (typeof lat !== "number" || typeof lng !== "number") return null;
      const key = labelKey(lat, lng);
      return labels[key];
    },
    [labels]
  );

  return { labels, getLabel };
};

export default useReverseGeocoded;
